import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import type { Destination, Profile, UserView } from '../api';
import { Banner } from './ui';
import { Button, Icon } from '../ui';
import { useSetupState } from './setup';
import { SetupStepObs } from './SetupStepObs';
import { SetupStepDestinations } from './SetupStepDestinations';
import { SetupStepDone } from './SetupStepDone';

export type Step = 1 | 2 | 3;
const STEPS: { n: Step; label: string }[] = [
  { n: 1, label: 'Connect OBS' },
  { n: 2, label: 'Destinations' },
  { n: 3, label: 'All set' },
];

export const HEADING_ID = 'setup-heading';

/** Props every step gets from the shell. */
export interface StepProps {
  view: UserView | null;
  profiles?: Profile[];
  dests?: Destination[];
  headingRef: RefObject<HTMLHeadingElement | null>;
  go: (step: Step, focusProfile?: string) => void;
}

/** Step heading: target of the focus move when the step changes. */
export function StepHeading({ headingRef, children, sub }: { headingRef: RefObject<HTMLHeadingElement | null>; children: ReactNode; sub?: ReactNode }) {
  return (
    <div>
      <h1 id={HEADING_ID} ref={headingRef} tabIndex={-1} className="m-0 text-2xl font-semibold tracking-[-.01em] outline-none">
        {children}
      </h1>
      {sub && <p className="mt-1.5 mb-0 text-[15px] text-text-soft">{sub}</p>}
    </div>
  );
}

/**
 * Footer of a step. Desktop: secondary actions left, the primary right.
 * Phone: sticks to the bottom of the screen, the primary on top at full width.
 */
export function StepFooter({ left, hint, primary }: { left: ReactNode; hint?: ReactNode; primary: ReactNode }) {
  return (
    <div className="sticky bottom-0 z-10 flex flex-col-reverse gap-2 border-t border-line bg-panel px-4 py-3 md:static md:flex-row md:items-center md:justify-between md:gap-4 md:px-7 md:py-[18px]">
      <div className="flex items-center justify-center gap-4 md:justify-start">{left}</div>
      <div className="flex flex-col items-stretch gap-2 md:flex-row md:items-center md:gap-3">
        {hint && <span className="hidden text-[13px] text-muted md:inline">{hint}</span>}
        {primary}
      </div>
    </div>
  );
}

export const linkBtn = 'min-h-[44px] px-1 text-sm text-muted hover:text-text md:min-h-9';

/**
 * First-run guide (docs/m15-setup-wizard.md): shown on the Live screen instead
 * of its content while me.setup is pending. ✕ dismisses it, Finish completes
 * it; both only after the server confirms, everything created stays.
 */
export function SetupWizard({ view, profiles, dests }: { view: UserView | null; profiles?: Profile[]; dests?: Destination[] }) {
  const [step, setStep] = useState<Step>(1);
  const [focusProfile, setFocusProfile] = useState<string | undefined>();
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const first = useRef(true);
  const setup = useSetupState();
  const focusRef = useRef<string | undefined>(undefined);
  focusRef.current = focusProfile;

  // New step: focus its heading, unless it was opened for one tile (its key field takes the focus).
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    if (!focusRef.current) headingRef.current?.focus();
  }, [step]);

  const go = (s: Step, profile?: string) => {
    setFocusProfile(profile);
    setStep(s);
    window.scrollTo?.({ top: 0 });
  };
  const props: StepProps = { view, profiles, dests, headingRef, go };

  return (
    // ConnectWizard: a panel with the steps across its top edge (.sx-steps): done steps in
    // text-soft with a check, the current one with the accent edge (AUDIT WZ-01)
    <section data-testid="setup-wizard" aria-labelledby={HEADING_ID} className="sx-panel flex flex-col !p-0">
      <div className="flex items-stretch border-b border-line">
        <ol aria-label="Setup steps" className="sx-steps m-0 min-w-0 flex-1 list-none p-0">
          {STEPS.map(({ n, label }) => {
            const done = n < step;
            const cur = n === step;
            return (
              <li key={n} aria-current={cur ? 'step' : undefined} className={`sx-step min-w-0 !py-3 md:!px-5 ${done ? 'done' : cur ? 'cur' : ''} ${cur ? '' : 'max-md:!flex-none'}`}>
                <span aria-hidden className="n flex-none">{done ? <Icon name="check" size={12} /> : n}</span>
                <span className={`truncate ${cur ? 'font-medium' : 'max-md:hidden'}`}>
                  <span className="sr-only">Step {n}{done ? ', done' : ''}: </span>
                  {label}
                </span>
              </li>
            );
          })}
        </ol>
        <span className="hidden items-center border-t-2 border-line-2 px-4 lg:flex"><span className="sx-label whitespace-nowrap">First-time setup</span></span>
        <div className="flex items-center border-t-2 border-line-2 pr-3 md:pr-4">
          <Button variant="ghost" icon data-testid="setup-close" aria-label="Close setup and open the dashboard"
            title="Close. You can reopen it from Setup guide at the top." disabled={!!setup.busy}
            onClick={() => void setup.set('dismissed')} className="max-md:!h-11 max-md:!w-11">
            <Icon name="close" size={16} />
          </Button>
        </div>
      </div>

      {setup.error && (
        <div className="flex flex-wrap items-center gap-3 px-4 pt-4 md:px-7">
          <div className="min-w-0 flex-1">
            <Banner tone="err">Couldn't save that: {setup.error}</Banner>
          </div>
          <Button className="max-md:min-h-[44px]" onClick={() => void setup.retry()}>
            <Icon name="refresh" size={16} />Try again
          </Button>
        </div>
      )}

      <div data-testid="setup-step" data-step={step} className="flex flex-col">
        {step === 1 && <SetupStepObs {...props} />}
        {step === 2 && <SetupStepDestinations {...props} focusProfile={focusProfile} />}
        {step === 3 && <SetupStepDone {...props} finishing={setup.busy === 'completed'} onFinish={() => void setup.set('completed')} />}
      </div>
    </section>
  );
}
